/**
 * Nexuma — PWAOverlay
 *
 * Note: the install banner and iOS install instructions are handled by the
 * pre-existing `PWAInstallBanner` (rendered from Dashboard.js), backed by the
 * `usePWAInstall` hook. To avoid two competing banners on screen at the same
 * time, this overlay no longer renders an install UI — it only handles the
 * service-worker update toast, which is orthogonal to install flow and not
 * covered elsewhere.
 *
 * Këtu rri edhe **gjendja pa lidhje**, dhe për një arsye praktike: faqja e
 * `offline.html` shihet vetëm kur aplikacioni NUK arrihet fare të hapet. Rasti
 * i zakonshëm është tjetri — guaska është e hapur nga cache-i, përdoruesi po
 * punon, dhe rrjeti bie në mes. Deri tani ai çast dukej si gabime të
 * shpërndara nëpër ekrane («nuk u ngarkua»), pa e thënë kurrë shkakun e vetëm
 * që i shpjegon të gjitha.
 */

import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, Slide, Box, Typography, Button } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';

import { subscribeUpdates, applyUpdate } from '../../services/pwa/swUpdate';

function SlideUp(props) {
  return <Slide {...props} direction="up" />;
}

export default function PWAOverlay() {
  const [updateReady, setUpdateReady] = useState(false);
  // `navigator.onLine` lexohet si vlerë fillestare, jo vetëm te ngjarjet: një
  // faqe e hapur nga cache-i kur rrjeti tashmë ka rënë nuk merr asnjë ngjarje
  // `offline` — ajo lind e tillë.
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => subscribeUpdates((s) => setUpdateReady(s.hasUpdate)), []);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // Pa lidhje, njoftimi i përditësimit nuk ka kuptim — versioni i ri nuk
  // shkarkohet dot gjithsesi. Njëri lajm në ekran, ai që e shpjegon çastin.
  if (offline) {
    return (
      <Snackbar
        open
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideUp}
        className="nexuma-install-banner"
        sx={{ maxWidth: 'min(560px, calc(100vw - 16px))' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<WifiOffRoundedIcon />}
          sx={{
            borderRadius: 3,
            alignItems: 'center',
            backgroundColor: 'rgba(46, 61, 82, 0.95)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            '& .MuiAlert-message': { flex: 1, py: 0.5 },
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff' }}>
              Nuk ke lidhje interneti
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)' }}>
              Po shihen të dhënat e fundit të ngarkuara. Ndryshimet nuk ruhen derisa lidhja të kthehet.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Snackbar
      open={updateReady}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideUp}
      className="nexuma-install-banner"
      sx={{
        // MUI-ja e qendërzon `bottom center` me `left: 50%` + `translateX(-50%)`
        // nga `sm` e lart. Mbishkrimi i mëparshëm `left/right: auto` e priste
        // pikërisht atë ankorim: elementi mbetej pa pikë nisjeje dhe transformi
        // e hidhte 50% majtas nga vendi i tij — prandaj njoftimi i përditësimit
        // dilte i ngjitur në buzën e majtë, mbi menunë anësore. Gjerësia
        // kufizohet, pozicionin e lëmë të temës.
        maxWidth: 'min(560px, calc(100vw - 16px))',
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<RefreshRoundedIcon />}
        sx={{
          borderRadius: 3,
          alignItems: 'center',
          backgroundColor: 'rgba(46, 61, 82, 0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          '& .MuiAlert-message': { flex: 1, py: 0.5 },
        }}
        action={
          <Button
            size="small"
            variant="contained"
            onClick={applyUpdate}
            sx={{
              ml: 1,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            Përditëso
          </Button>
        }
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff' }}>
            Version i ri i disponueshëm
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)' }}>
            Përditëso për të marrë veçoritë e fundit.
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  );
}
